import _ from 'lodash';
import React, {useEffect, useState} from "react";
import {NavHashLink, HashLink} from 'react-router-hash-link';
import {useNavigate, useMatch} from "react-router-dom";
import { useTranslation, initReactI18next } from "react-i18next";
import {push as Menu} from 'react-burger-menu';
import classNames from "classnames";
import SocialIcons from "./SocialIcons";
import useMediaQuery from "../hooks/useMediaQuery";

import hamburgerIcon from '../assets/icons/navbar/menu@2x.png';
import hamburgerXIcon from '../assets/icons/navbar/menu-x@2x.png';
import {Button} from "./Button";

const THEME_STORAGE_KEY = 'LAVITA_THEME';
const NavLink = NavHashLink;
const SINGLE_PAGE_ROUTES = {
    home: '/#home',
    intelligence: '/#intelligence',
    privacyInfrastructure: '/#privacy-infrastructure',
    connectedExperience: '/#connected-experience',
    ecosystem: '/#ecosystem',
    about: '/#about',
    news: '/#news',
    platform: '/#overview',
    userWorkflow: '/#user-workflow',
    components: '/#key-components',
    decentralizedBlockchain: '/#decentralized-blockchain',
    secureDataStorage: '/#secure-data-storage',
    secureComputing: '/#secure-computing',
    aiPoweredResearch: '/#ai-powered-research',
    secureKeyManagement: '/#secure-key-management',
    roadmap: '/#roadmap',
    apps: '/#apps',
    docs: '/#docs',
    download: '/#download',
};

const scrollToHashElement = (element) => {
    if (!element) {
        return;
    }

    element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
    });
};

const scrollToHashTarget = (to) => {
    if (!_.isString(to) || !to.includes('#')) {
        return;
    }

    const targetId = decodeURIComponent(to.split('#')[1]);

    window.requestAnimationFrame(() => {
        scrollToHashElement(document.getElementById(targetId));
    });
};

const getInitialTheme = () => {
    if (typeof window === 'undefined') {
        return 'dark';
    }

    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

    if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
    }

    return 'dark';
};

const Logo = ({className = ''}) => (
    <span className={classNames('Navbar__logo-stack', className)}>
        <img src={'/android-chrome-192x192.png'}
             className={'Navbar__logo-icon'}
             alt={'Lavita'}
        />
    </span>
);

export const HeaderLink = ({to, target, children, className, onClick, as}) => {
    if(target){
        return (
            <a href={to}
               target={target}
               className={classNames("NavbarLink", className)}
               onClick={onClick}
            >
                {children}
            </a>
        );
    }
    if(_.isNil(to)){
        return (
            <div
                className={"NavbarLink"}
                onClick={onClick}
            >
                {children}
            </div>
        )
    }
    return (
        <NavLink to={to}
                 target={target}
                 className={({ isActive }) => classNames("NavbarLink", {
                     'NavbarLink--active': isActive
                 }, className)}
                 smooth
                 scroll={scrollToHashElement}
                 onClick={(event) => {
                     if (onClick) {
                         onClick(event);
                     }

                     scrollToHashTarget(to);
                 }}
        >
            {children}
        </NavLink>
    );
}

const SubMenuLink = ({to, target, children}) => {
    return (
        <li className={'NavbarLink__submenu-item'}
            onClick={(e)=> {
                e.stopPropagation();
            }}
        >
            {
                to.startsWith('http') ?
                    <a href={to}
                       target={'_blank'}
                       rel={'noreferrer'}
                    >
                        {children}
                    </a>
                    :
                    <HashLink to={to}
                              smooth
                              scroll={scrollToHashElement}
                              onClick={() => scrollToHashTarget(to)}>
                        {children}
                    </HashLink>
            }
        </li>
    );
}

export const BottomNavigator = ({prev, next}) => {
    return (
        <div className={'BottomNavigator'}>
            {
                prev &&
                <NavLink to={prev.to}
                         // className='NavbarLink'
                >
                    {`< ${prev.title}`}
                </NavLink>
            }
            {
                next &&
                <NavLink to={next.to}
                         // className='NavbarLink'
                >
                    {`${next.title} >`}
                </NavLink>
            }
        </div>
    )
}

const NavCaret = () => (
    <svg className="NavbarLink__caret" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Label + rotating caret for nav items that have a dropdown.
const NavLabel = ({children}) => (
    <span className="NavbarLink__label">{children}<NavCaret/></span>
);

export const Navbar = ({}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(768);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [theme, setTheme] = useState(getInitialTheme);
    const isStakePage = useMatch("/stake");
    const isLightTheme = theme === 'light';

    useEffect(() => {
        document.body.classList.toggle('Theme--light', isLightTheme);
        document.body.classList.toggle('Theme--dark', !isLightTheme);
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme, isLightTheme]);

    const onOpenClick = () => {
        setIsMenuOpen(true);
    };
    const onCloseClick = () => {
        setIsMenuOpen(false);
    };
    const navigateToSection = (to) => {
        onCloseClick();
        navigate(to);
        scrollToHashTarget(to);
    };
    const onMenuStateChange = (state) => {
        setIsMenuOpen(state.isOpen);
    };
    const onThemeToggle = () => {
        setTheme((currentTheme) => currentTheme === 'light' ? 'dark' : 'light');
    };
    const themeToggle = (
        <button type={'button'}
                className={classNames('NavbarThemeToggle', {
                    'NavbarThemeToggle--light': isLightTheme
                })}
                onClick={onThemeToggle}
                aria-label={isLightTheme ? t('navbar.switch_to_dark') : t('navbar.switch_to_light')}
        >
            <span className={'NavbarThemeToggle__icon'}>
                {isLightTheme ? 'D' : 'L'}
            </span>
            <span className={'NavbarThemeToggle__label'}>
                {isLightTheme ? t('navbar.theme_dark') : t('navbar.theme_light')}
            </span>
        </button>
    );
    const links = (
        <>
            <HeaderLink to={(isMobile ? SINGLE_PAGE_ROUTES.about : null)}
                        onClick={() => {
                            if(!isMobile){
                                navigateToSection(SINGLE_PAGE_ROUTES.about);
                                return;
                            }

                            onCloseClick();
                        }}
            >
                {t('navbar.about')}
            </HeaderLink>
            <HeaderLink
                to={(isMobile ? SINGLE_PAGE_ROUTES.platform : null)}
                onClick={() => {
                    if(!isMobile){
                        navigateToSection(SINGLE_PAGE_ROUTES.platform);
                        return;
                    }

                    onCloseClick();
                }}
            >
                <NavLabel>{t('navbar.platform')}</NavLabel>
                <ul className="NavbarLink__submenu">
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.platform}>
                        {t('navbar.lavita_platform')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.userWorkflow}>
                        {t('navbar.user_workflow')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.apps}>
                        {t('navbar.apps')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.download}>
                        {t('navbar.download_app')}
                    </SubMenuLink>
                </ul>
            </HeaderLink>
            <HeaderLink to={(isMobile ? SINGLE_PAGE_ROUTES.components : null)}
                        onClick={() => {
                            if(!isMobile){
                                navigateToSection(SINGLE_PAGE_ROUTES.components);
                                return;
                            }

                            onCloseClick();
                        }}
            >
                <NavLabel>{t('navbar.technology')}</NavLabel>
                <ul className="NavbarLink__submenu">
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.components}>
                        {t('navbar.key_components')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.decentralizedBlockchain}>
                        {t('navbar.decentralized_blockchain')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.secureDataStorage}>
                        {t('navbar.secure_data_storage')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.secureComputing}>
                        {t('navbar.secure_computing')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.aiPoweredResearch}>
                        {t('navbar.ai_powered_research')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.secureKeyManagement}>
                        {t('navbar.secure_key_management')}
                    </SubMenuLink>
                </ul>
            </HeaderLink>
            <HeaderLink to={'/podcast'} onClick={onCloseClick}>
                <NavLabel>{t('navbar.podcast')}</NavLabel>
                <ul className="NavbarLink__submenu">
                    <SubMenuLink to={'/podcast#episodes'}>
                        {t('navbar.episodes')}
                    </SubMenuLink>
                    <SubMenuLink to={'/podcast#about'}>
                        {t('navbar.about')}
                    </SubMenuLink>
                </ul>
            </HeaderLink>
            <HeaderLink to={'/articles'} onClick={onCloseClick}>
                <NavLabel>{t('navbar.articles')}</NavLabel>
                <ul className="NavbarLink__submenu">
                    <SubMenuLink to={'/articles#articles'}>
                        {t('navbar.all_articles')}
                    </SubMenuLink>
                    <SubMenuLink to={'/articles#about'}>
                        {t('navbar.about')}
                    </SubMenuLink>
                </ul>
            </HeaderLink>
            <HeaderLink to={(isMobile ? SINGLE_PAGE_ROUTES.roadmap : null)}
                        onClick={() => {
                            if(!isMobile){
                                navigateToSection(SINGLE_PAGE_ROUTES.roadmap);
                                return;
                            }

                            onCloseClick();
                        }}
            >
                <NavLabel>{t('navbar.resources')}</NavLabel>
                <ul className="NavbarLink__submenu">
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.roadmap}>
                        {t('navbar.roadmap')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.news}>
                        {t('navbar.news')}
                    </SubMenuLink>
                    <SubMenuLink to={SINGLE_PAGE_ROUTES.docs}>
                        {t('navbar.docs')}
                    </SubMenuLink>
                </ul>
            </HeaderLink>
            <HeaderLink to={'/stake'}
                        className={'mobile-only'}
                        onClick={onCloseClick}
            >
                {t('navbar.stake')}
            </HeaderLink>
        </>
    );
    const stakeButton = (
        <Button className={'Navbar__stake-button'}
                color={'green'}
                size={'small'}
                onClick={() => {
                    navigate("/stake");
                }}
        >
            {t('navbar.stake')}
        </Button>
    );
    return (
        <div className={'Navbar'}>
            {
                isMobile &&
                <React.Fragment>
                    <a onClick={onOpenClick}
                       className={'NavbarHamburgerButton'}
                    >
                        <img src={hamburgerIcon}/>
                    </a>
                    <div className={'Navbar__content'}>
                        <NavLink to={'/'}
                                 className={'Navbar__logo-wrapper'}>
                            <Logo className={'mobile-only'}/>
                        </NavLink>
                        <div className={'Navbar__right-mobile'}>
                            {themeToggle}
                            {
                                !isStakePage &&
                                stakeButton
                            }
                        </div>
                    </div>
                    <Menu isOpen={isMenuOpen}
                          disableAutoFocus
                          itemListElement='div'
                          customBurgerIcon={false}
                          customCrossIcon={false}
                          onStateChange={onMenuStateChange}>
                        <div className={'NavBarMenuHeader'}>
                            <a onClick={onCloseClick}
                               className={'NavBarMenuHeader__close'}>
                                <img src={hamburgerXIcon}/>
                            </a>
                            {links}
                        </div>
                        <SocialIcons/>
                    </Menu>
                </React.Fragment>
            }
            {
                !isMobile &&
                <div className={'Navbar__content'}>
                    <div className={'Navbar__links'}>
                        <NavLink to={'/'}>
                            <Logo/>
                        </NavLink>
                        {links}
                        {
                            !isStakePage &&
                            stakeButton
                        }
                    </div>
                    <div className={'Navbar__right'}>
                        {themeToggle}
                        <SocialIcons/>
                    </div>
                </div>
            }

        </div>
    )
}
